import { parseISO } from "date-fns";
import jwtDecode, { JwtPayload } from "jwt-decode";
import User from "../interfaces/User";
import { RegisterType } from "../common/interfaces/enums/RegisterType";
import { PermissionType } from "../common/interfaces/enums/PermissionType";
import { UserType } from "../common/interfaces/enums/UserType";
import http, { tokenKey } from "../common/services/httpService";
import { AxiosResponse } from "axios";
import { stringifyBgToIso } from "../common/utils/dateTime";

interface JwtPayloadExtended extends JwtPayload {
  given_name: string;
  userType: UserType;
  permissions: Array<string>;
}

const apiEndpoint = "/authentication";
export const signature_not_after = "signature_not_after";

const getJwt = (): string | null => {
  return localStorage.getItem(tokenKey);
};

type TokenDto = { token?: string };

export type LoginDto = {
  uid: string;

  isAuthenticated?: boolean;
  has2fa?: boolean;

  notBefore?: Date;
  notAfter?: Date;
  thumbprint?: string;
} & TokenDto;

type PasswordLoginDto = {
  username: string;
  password: string;
} & TokenDto;

const initLocalStorage = (model: LoginDto) => {
  if (model.token) localStorage.setItem(tokenKey, model.token);
  if (model.notAfter) localStorage.setItem(signature_not_after, stringifyBgToIso(model.notAfter));
};

const login = async (uid: string): Promise<AxiosResponse<LoginDto>> => {
  const model: LoginDto = { uid };
  return http.post<LoginDto>(apiEndpoint, model);
};

const loginDev = async (): Promise<AxiosResponse<LoginDto>> => {
  return await http.post<LoginDto>(`${apiEndpoint}/devlogin`);
};

const loginAdmin = async (username: string, password: string): Promise<AxiosResponse<LoginDto>> => {
  const model: PasswordLoginDto = { username, password };
  return await http.post<LoginDto>(`${apiEndpoint}/admin`, model);
};

const loginExternalUser = async (
  token: string,
  username: string,
  password: string
): Promise<AxiosResponse<LoginDto>> => {
  const model: PasswordLoginDto = { token, username, password };
  return await http.post<LoginDto>(`${apiEndpoint}/external`, model);
};

const logout = (): void => {
  localStorage.clear();
};

const getUser = (): User | null => {
  const jwt = getJwt();
  try {
    const {
      sub,
      given_name: name,
      exp,
      userType: userTypeStr,
      permissions: permissionsArray,
    } = jwtDecode<JwtPayloadExtended>(jwt as string);

    const isExpired = exp === undefined || (exp && exp < Date.now() / 1000);

    if (isExpired) {
      localStorage.clear();
      return null;
    }

    const userId = parseInt(sub as string);
    const userType = userTypeStr.toString() as UserType;
    const isAdmin = userType === UserType.Admin;
    const isExternalUser = userType === UserType.ExternalUser;
    const permissions = {} as Record<RegisterType, PermissionType>;

    if (permissionsArray) {
      if (Array.isArray(permissionsArray)) {
        permissionsArray?.forEach((pair) => {
          const ps = pair.split(":");
          const key = ps[0] as RegisterType;
          const value = parseInt(ps[1]) as PermissionType;

          permissions[key] = value;
        });
      } else {
        const ps = (permissionsArray as any).split(":");
        const key = ps[0] as RegisterType;
        const value = parseInt(ps[1]) as PermissionType;

        permissions[key] = value;
      }
    }

    // enable Authorizations menu / link
    // const all = Object.keys(permissions) as Array<RegisterType>;

    // const hasAuthorizations = _.includes(all, RegisterType.Authorizations);
    // if (!hasAuthorizations) {
    //   let hasAuthorizationType: boolean = false;
    //   for (let i = 0; i < all.length; i++) {
    //     hasAuthorizationType = _.includes(AuthorizationTypes, all[i]);
    //     if (hasAuthorizationType) break;
    //   }
    // }

    // if (!hasAuthorizations) permissions[RegisterType.Authorizations] = 2;

    return { userId, name, userType, isAdmin, isExternalUser, permissions };
  } catch (ex) {
    return null;
  }
};

const getSignatureExpireDate = (days: number): Date | null => {
  const notAfter = localStorage.getItem(signature_not_after);
  if (notAfter) {
    const notAfterDate = parseISO(notAfter);
    const calculatedDate = new Date();
    calculatedDate.setDate(calculatedDate.getDate() + days);
    if (notAfterDate.getTime() < calculatedDate.getTime()) return notAfterDate;
  }
  return null;
};

export default {
  login,
  logout,
  getUser,
  loginDev,
  loginAdmin,
  loginExternalUser,
  getSignatureExpireDate,
  initLocalStorage,
};
